import * as React from 'react';
import { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';

import { AccountInfoContext } from '../account/account-info';
import { InteractiveModal } from '../ui-elements/modals/InteractiveModal';
import { Button } from '../ui-elements/Main/Button';
import { colors } from '../ui-elements/Main/MainTheme';

export interface TrialPhoneVerifyResult {
  selfTicketUuid?: string;
  subdomain?: string;
}

interface TrialPhoneVerifyModalProps {
  show: boolean;
  onClose: () => void;
  onVerified: (result?: TrialPhoneVerifyResult) => void;
}

type Step = 'enter_phone' | 'enter_code';

const csrfToken = () =>
  (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? '';

const postJson = async (route: string, payload: Record<string, string>) => {
  const res = await fetch(route, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
};

// Format a raw 10-digit US/CA number as "(415) 555-1234" while the user types.
// We always store digits-only in state and format only at render time.
const formatNANP = (digits: string) => {
  const d = (digits || '').replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

// Server expects E.164 (+1XXXXXXXXXX); we store 10 raw digits in state.
const toE164 = (digits: string) => {
  const d = (digits || '').replace(/\D/g, '').slice(0, 10);
  return d.length === 10 ? `+1${d}` : '';
};

export const TrialPhoneVerifyModal = ({
  show,
  onClose,
  onVerified,
}: TrialPhoneVerifyModalProps) => {
  const { subdomain } = useContext(AccountInfoContext);
  const [step, setStep] = useState<Step>('enter_phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneFormatted, setPhoneFormatted] = useState<string>('');

  const reset = useCallback(() => {
    setStep('enter_phone');
    setPhone('');
    setCode('');
    setError(null);
    setSubmitting(false);
    setPhoneFormatted('');
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  // We store 10 digits (no country code) in state and prepend +1 on submit.
  const phoneDigits = phone.replace(/\D/g, '');
  const phoneComplete = phoneDigits.length === 10;

  const submitPhone = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting || !phoneComplete) return;
    setSubmitting(true);
    setError(null);
    try {
      const { ok, body } = await postJson('/v1/phone_verifications', {
        subdomain,
        phone: toE164(phone),
      });
      if (!ok) {
        setError(body?.errors?.[0] ?? 'Could not send verification code. Try again.');
        return;
      }
      setPhoneFormatted(body?.data?.phone_formatted ?? toE164(phone));
      setStep('enter_code');
    } catch (e) {
      setError('Network error. Try again.');
    } finally {
      setSubmitting(false);
    }
  }, [phone, phoneComplete, subdomain, submitting]);

  const submitCode = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { ok, body } = await postJson('/v1/phone_verifications/confirm', {
        subdomain,
        code: code.trim(),
      });
      if (!ok) {
        setError(body?.errors?.[0] ?? 'Invalid or expired code.');
        return;
      }
      // V1 controllers wrap success payloads under `data:` (see Respondable
      // concern). Errors live top-level. So self_ticket is at body.data.self_ticket.
      const selfTicket = body?.data?.self_ticket;
      const result: TrialPhoneVerifyResult | undefined = selfTicket
        ? { selfTicketUuid: selfTicket.uuid, subdomain: selfTicket.subdomain }
        : undefined;
      onVerified(result);
      handleClose();
    } catch (e) {
      setError('Network error. Try again.');
    } finally {
      setSubmitting(false);
    }
  }, [code, subdomain, submitting, onVerified, handleClose]);

  return (
    <InteractiveModal hide={!show} width="540px" height="auto">
      <Container>
        <Header>Verify your mobile phone</Header>
        <Subheader>
          Free trial accounts can only send SMS to their own verified mobile
          number. Add yours to begin testing.
        </Subheader>

        {step === 'enter_phone' && (
          <Form onSubmit={submitPhone}>
            <Label htmlFor="trial-verify-phone">Mobile phone number</Label>
            <PhoneInputRow $valid={phoneComplete}>
              <CountryPrefix>+1</CountryPrefix>
              <DigitsInput
                id="trial-verify-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="(555) 555-1234"
                value={formatNANP(phoneDigits)}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                required
                autoFocus
              />
            </PhoneInputRow>
            <Hint>US or Canadian mobile only. We&apos;ll text you a 6-digit code.</Hint>
            {/*
              TCPA / CTIA / Twilio TCR opt-in disclosure. Same legal
              footprint as the corresponding block in TrialSurveyModal
              Step 4 — required at every phone-collection surface, not
              just one. Includes (a) express consent, (b) carrier rates,
              (c) STOP / HELP instructions, (d) message-frequency,
              (e) Privacy + Terms links opened in a new tab so the
              modal state isn't lost.
            */}
            <TcpaDisclosure>
              By providing your phone number, you agree to receive SMS
              messages from Avochato. Msg &amp; data rates may apply. Msg
              frequency varies. Reply STOP to opt out, HELP for help.
              <br />
              <TcpaLinks>
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                <span aria-hidden> | </span>
                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
              </TcpaLinks>
            </TcpaDisclosure>
            {error && <Error>{error}</Error>}
            <Actions>
              <Button theme="cancel" size="medium" type="button" onClick={handleClose}>
                Cancel
              </Button>
              <Button theme="primary" size="medium" type="submit" disabled={submitting || !phoneComplete}>
                {submitting ? 'Sending…' : 'Send code'}
              </Button>
            </Actions>
          </Form>
        )}

        {step === 'enter_code' && (
          <Form onSubmit={submitCode}>
            <Label>
              Enter the 6-digit code sent to <PhoneNumber>{phoneFormatted}</PhoneNumber>
            </Label>
            <CodeInput
              type="tel"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              placeholder="••••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              required
              autoFocus
            />
            <Hint>Code expires in 10 minutes.</Hint>
            {error && <Error>{error}</Error>}
            <Actions>
              <Button
                theme="cancel"
                size="medium"
                type="button"
                onClick={() => { setStep('enter_phone'); setError(null); setCode(''); }}
              >
                Use a different number
              </Button>
              <Button theme="primary" size="medium" type="submit" disabled={submitting || code.length !== 6}>
                {submitting ? 'Verifying…' : 'Verify'}
              </Button>
            </Actions>
          </Form>
        )}
      </Container>
    </InteractiveModal>
  );
};

const Container = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  /* Roomier padding — gives the title + form some breathing room and
     matches the proportions of the in-app verify pill we use elsewhere. */
  padding: 40px 44px 36px;
  width: 100%;
  font-family: Montserrat, sans-serif;
`;

const Header = styled.h2`
  font-size: 26px;
  /* Bolder title (was 700). Reads as the headline rather than a label. */
  font-weight: 800;
  color: ${colors.darkGreen};
  margin-top: 0;
  margin-bottom: 0;
  line-height: 1.25;
  letter-spacing: -0.2px;
`;

const Subheader = styled.p`
  font-size: 14px;
  color: ${colors.gray};
  margin-top: 14px;
  margin-bottom: 22px;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.textBlack};
  margin-bottom: 6px;
`;

const PhoneNumber = styled.span`
  color: ${colors.darkGreen};
  font-weight: 700;
`;

// Custom phone input: a fixed "+1" prefix box on the left and a 10-digit
// formatted input on the right. We only support US/CA in v1 so the country
// dropdown that ships with react-phone-input-2 is overkill (and was rendering
// a janky empty flag well that bothered us). This is plain styled inputs,
// no external lib, no asset-pipeline flag sprite to worry about.
// Note: declare props WITHOUT `$` prefix — styled-components v6's
// MakeTransient<Props> auto-adds the `$` for you (see CLAUDE.md ->
// "Styled-components v6 transient props"). Adding it here would
// produce `$$valid` and break the JSX `$valid={...}` usage.
const PhoneInputRow = styled.div<{ valid: boolean }>`
  display: flex;
  align-items: stretch;
  border: 1px solid ${({ $valid }) => ($valid ? colors.darkGreen : colors.greyer)};
  border-radius: 6px;
  overflow: hidden;
  background: ${colors.white};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${colors.darkGreen};
    box-shadow: 0 0 0 2px rgba(30, 112, 90, 0.18);
  }
`;

const CountryPrefix = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  background: ${colors.lightestGray};
  border-right: 1px solid ${colors.greyer};
  font-size: 15px;
  font-weight: 600;
  color: ${colors.textBlack};
  font-family: Montserrat, sans-serif;
  user-select: none;
`;

const DigitsInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 14px;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  color: ${colors.textBlack};
  background: ${colors.white};

  &::placeholder {
    color: ${colors.steel};
  }
`;

const CodeInput = styled.input`
  padding: 12px 16px;
  border: 1px solid ${colors.greyer};
  border-radius: 4px;
  font-size: 22px;
  letter-spacing: 8px;
  text-align: center;
  font-family: Montserrat, sans-serif;
  color: ${colors.textBlack};

  &:focus {
    border-color: ${colors.darkGreen};
    box-shadow: 0 0 0 1px ${colors.darkGreen};
    outline: none;
  }

  &::placeholder {
    color: ${colors.greyer};
    letter-spacing: 8px;
  }
`;

const Hint = styled.span`
  font-size: 12px;
  color: ${colors.steel};
  margin-top: 4px;
`;

/*
  TCPA / CTIA / Twilio TCR opt-in disclosure. Same audit-safe shape as
  the corresponding block in TrialSurveyModal — small, quiet styling
  but inline-visible on the same page as the phone field.
*/
const TcpaDisclosure = styled.div`
  font-size: 11px;
  line-height: 1.45;
  color: ${colors.steel};
  margin-top: 10px;
`;

const TcpaLinks = styled.span`
  display: inline-block;
  margin-top: 4px;

  a {
    color: ${colors.steel};
    text-decoration: underline;
    &:hover { color: ${colors.darkGreen}; }
  }

  span {
    margin: 0 4px;
    color: ${colors.greyer};
  }
`;

const Error = styled.div`
  background: ${colors.errorRed};
  color: ${colors.red};
  padding: 10px 14px;
  border-radius: 4px;
  font-size: 13px;
  margin-top: 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 28px;
`;
